// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * GameOverScreen - Reusable game over screen for game boards.
 *
 * Provides a consistent game over UI with:
 * - Winner announcement (individual or team)
 * - Final standings/scores (players or teams)
 * - Play Again button (host only)
 * - Return Home button (all players)
 *
 * Supports both individual-scoring games and team-based games.
 */

import type { ReactNode } from 'react';

export interface PlayerScore {
  /** Player ID */
  player_id: string;
  /** Display name */
  display_name: string;
  /** Final score */
  score: number;
}

export interface TeamScore {
  /** Team identifier */
  team_id: string;
  /** Team display name (e.g., "Blue Team") */
  team_name: string;
  /** Team emoji (e.g., "🔵") */
  team_emoji: string;
  /** Team color for styling (CSS color) */
  team_color: string;
  /** Team's final score */
  score: number;
  /** Optional score label (e.g., "4/9 agents") */
  scoreLabel?: string;
  /** Team member display names */
  members: string[];
}

export interface GameOverScreenProps {
  /** Optional custom title (defaults to "Game Over!") */
  title?: string;
  /** Optional custom winner title (defaults to "The Winner is...") */
  winnerTitle?: string;
  /** Winner's display name (for individual games) */
  winnerName?: string;
  /** Winner's score (optional, displayed if provided) */
  winnerScore?: number;
  /** Current player's ID (for highlighting in individual standings) */
  currentPlayerId: string;
  /** Whether current player is host */
  isHost: boolean;
  /** Array of player scores for individual-scoring games (sorted by rank) */
  players?: PlayerScore[];
  /** Array of team scores for team-based games (sorted by rank) */
  teams?: TeamScore[];
  /** Winning team ID (for team-based games) */
  winningTeam?: string;
  /** Handler for Play Again button (host only). Omit for boards without replay. */
  onPlayAgain?: () => void;
  /** Handler for Return Home button */
  onReturnHome: () => void;
  /** Optional extra content between winner and standings */
  children?: ReactNode;
  /** Data testid prefix (e.g., "faux_facts" -> "faux_facts-play-again-button") */
  testIdPrefix?: string;
  /** Game name/emoji for styling (optional) */
  gameEmoji?: string;
}

/**
 * Standard game over screen for game boards.
 *
 * @example Individual scoring:
 * ```tsx
 * <GameOverScreen
 *   winnerName={winner.display_name}
 *   winnerScore={winner.score}
 *   currentPlayerId={playerId}
 *   isHost={isHost}
 *   players={sortedPlayers}
 *   onPlayAgain={handlePlayAgain}
 *   onReturnHome={handleReturnHome}
 *   testIdPrefix="faux_facts"
 * />
 * ```
 *
 * @example Team-based scoring:
 * ```tsx
 * <GameOverScreen
 *   currentPlayerId={playerId}
 *   isHost={isHost}
 *   teams={[
 *     { team_id: 'blue', team_name: 'Blue Team', team_emoji: '🔵', team_color: '#3b82f6', score: 9, members: ['Alice', 'Bob'] },
 *     { team_id: 'red', team_name: 'Red Team', team_emoji: '🔴', team_color: '#ef4444', score: 7, members: ['Charlie', 'Dave'] },
 *   ]}
 *   winningTeam="blue"
 *   onPlayAgain={handlePlayAgain}
 *   onReturnHome={handleReturnHome}
 *   testIdPrefix="cipher"
 * />
 * ```
 */
export function GameOverScreen({
  title = 'Game Over!',
  winnerTitle = 'The Winner is...',
  winnerName,
  winnerScore,
  currentPlayerId,
  isHost,
  players,
  teams,
  winningTeam,
  onPlayAgain,
  onReturnHome,
  children,
  testIdPrefix,
  gameEmoji = '🏆',
}: GameOverScreenProps) {
  const playAgainTestId = testIdPrefix
    ? `${testIdPrefix}-play-again-button`
    : 'game-over-play-again-button';
  const returnHomeTestId = testIdPrefix
    ? `${testIdPrefix}-return-home-button`
    : 'game-over-return-home-button';

  // Determine if this is a team-based game
  const isTeamGame = teams && teams.length > 0;
  const winningTeamData = isTeamGame ? teams.find(t => t.team_id === winningTeam) : null;

  // For team games, derive winner info from winning team
  const displayWinnerName =
    isTeamGame && winningTeamData
      ? `${winningTeamData.team_emoji} ${winningTeamData.team_name}`
      : winnerName || 'Unknown';
  const displayWinnerScore =
    isTeamGame && winningTeamData
      ? winningTeamData.scoreLabel || `${winningTeamData.score} points`
      : winnerScore !== undefined
        ? `${winnerScore} points`
        : undefined;

  return (
    <div className="max-w-4xl mx-auto" data-testid="game-over-container">
      <div
        className="rounded-xl shadow-lg p-8 text-center"
        style={{
          backgroundColor: 'var(--color-accent)',
          color: 'var(--color-surface)',
        }}
      >
        <div className="text-6xl mb-4">{gameEmoji}</div>
        <h2 className="text-3xl font-bold mb-4">{title}</h2>

        {/* Winner announcement */}
        <div
          className="rounded-lg p-6 mb-6"
          style={{
            backgroundColor:
              isTeamGame && winningTeamData ? winningTeamData.team_color : 'rgba(255,255,255,0.2)',
          }}
          data-testid="game-over-winner-display"
        >
          <p className="text-xl mb-2">{winnerTitle}</p>
          <p className="text-4xl font-bold">{displayWinnerName}</p>
          {displayWinnerScore && <p className="text-2xl mt-2">{displayWinnerScore}</p>}
        </div>

        {children}

        {/* Team standings (for team-based games) */}
        {isTeamGame && teams.length > 0 && (
          <div
            className="rounded-lg p-6"
            style={{
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
            }}
            data-testid="game-over-final-scores"
          >
            <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--color-text-primary)' }}>
              Final Standings
            </h3>
            <div className="space-y-4">
              {teams.map((team, i) => (
                <div
                  key={team.team_id}
                  className="rounded-lg p-4"
                  style={{
                    backgroundColor:
                      i === 0 ? 'var(--color-accent-bg)' : 'var(--color-surface-elevated)',
                    border:
                      team.team_id === winningTeam
                        ? `3px solid ${team.team_color}`
                        : '1px solid var(--color-border)',
                  }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : `#${i + 1}`}
                      </span>
                      <span className="text-xl font-bold" style={{ color: team.team_color }}>
                        {team.team_emoji} {team.team_name}
                      </span>
                    </div>
                    <span
                      className="text-xl font-bold"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {team.scoreLabel || team.score}
                    </span>
                  </div>
                  {/* Team members */}
                  <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    {team.members.join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Individual player standings (for individual-scoring games) */}
        {!isTeamGame && players && players.length > 0 && (
          <div
            className="rounded-lg p-6"
            style={{
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
            }}
            data-testid="game-over-final-scores"
          >
            <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--color-text-primary)' }}>
              Final Standings
            </h3>
            <div className="space-y-2">
              {players.map((p, i) => (
                <div
                  key={p.player_id}
                  className="flex justify-between items-center p-3 rounded"
                  style={{
                    backgroundColor:
                      i === 0 ? 'var(--color-accent-bg)' : 'var(--color-surface-elevated)',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                    </span>
                    <span
                      className="font-medium"
                      style={{
                        color:
                          p.player_id === currentPlayerId
                            ? 'var(--color-accent)'
                            : 'var(--color-text-primary)',
                      }}
                    >
                      {p.display_name}
                      {p.player_id === currentPlayerId && ' (You)'}
                    </span>
                  </div>
                  <span className="font-bold" style={{ color: 'var(--color-text-primary)' }}>
                    {p.score}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-4 justify-center mt-6">
          {/* Play again button - host only, and only when a replay handler is provided */}
          {isHost && onPlayAgain && (
            <button
              onClick={onPlayAgain}
              className="px-6 py-3 rounded-lg font-bold"
              style={{
                backgroundColor: 'var(--color-surface)',
                color: 'var(--color-accent)',
              }}
              type="button"
              data-testid={playAgainTestId}
            >
              Play Again
            </button>
          )}

          {/* Return Home button - available to all players */}
          <button
            onClick={onReturnHome}
            className="px-6 py-3 rounded-lg font-bold"
            style={{
              backgroundColor: 'var(--color-surface-elevated)',
              color: 'var(--color-text-secondary)',
            }}
            type="button"
            data-testid={returnHomeTestId}
          >
            Return Home
          </button>
        </div>
      </div>
    </div>
  );
}
